import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, Button, Card, CardContent, Alert, RadioGroup, FormControlLabel, Radio, Grid } from '@mui/material';
import Spinner from '../../components/Spinner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const getLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DailyChallenge: React.FC = () => {
  const navigate = useNavigate();
  const [localDateKey, setLocalDateKey] = useState(() => getLocalDateKey(new Date()));
  const [challenge, setChallenge] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Challenge game state
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [graded, setGraded] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const fetchDailyChallenge = useCallback(async (dateKey: string) => {
    setLoading(true);
    setError(null);
    setChallenge(null);
    try {
      const res = await api.get(`/language-hub/daily?date=${encodeURIComponent(dateKey)}`);
      if (res.data && res.data.success) {
        setChallenge(res.data.data);
      } else {
        setError('Failed to fetch the daily challenge.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to daily challenge API');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedAnswer('');
    setGraded(false);
    setIsCorrect(false);
    fetchDailyChallenge(localDateKey);
  }, [fetchDailyChallenge, localDateKey]);

  useEffect(() => {
    let midnightTimer: number;
    const refreshAtMidnight = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setDate(nextMidnight.getDate() + 1);
      nextMidnight.setHours(0, 0, 0, 0);
      midnightTimer = window.setTimeout(() => {
        setLocalDateKey(getLocalDateKey(new Date()));
        refreshAtMidnight();
      }, Math.max(100, nextMidnight.getTime() - now.getTime() + 100));
    };
    const checkLocalDate = () => {
      const currentDateKey = getLocalDateKey(new Date());
      setLocalDateKey((previousDateKey) => previousDateKey === currentDateKey ? previousDateKey : currentDateKey);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkLocalDate();
    };

    refreshAtMidnight();
    const dateCheckInterval = window.setInterval(checkLocalDate, 60_000);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearTimeout(midnightTimer);
      window.clearInterval(dateCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleGrade = () => {
    if (!challenge || !challenge.quizzes || challenge.quizzes.length === 0) return;
    const correctAns = challenge.quizzes[0].correctAnswer;
    setIsCorrect(selectedAnswer === correctAns);
    setGraded(true);
  };

  const handleReset = () => {
    setSelectedAnswer('');
    setGraded(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back button */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/language-hub')} 
        sx={{ mb: 4 }}
      >
        Language Hub
      </Button>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Daily Challenge
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Boost your habits. Check in daily to learn a new Word of the Day and solve a quick mini-task.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Spinner />
        </Box>
      ) : !challenge ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">No challenges available today. Check back later!</Typography>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {/* Word of the Day Card */}
          <Card 
            sx={{ 
              p: 4, 
              borderRadius: 4, 
              mb: 5, 
              background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.05) 0%, rgba(234, 179, 8, 0.01) 100%)',
              border: '1px solid rgba(234, 179, 8, 0.2)' 
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <StarIcon sx={{ color: '#eab308' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#eab308', textTransform: 'uppercase', letterSpacing: 2 }}>
                  Word of the Day
                </Typography>
              </Box>

              <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 2, color: 'text.primary' }}>
                {challenge.word}
              </Typography>

              {challenge.partOfSpeech && (
                <Typography variant="subtitle1" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                  {challenge.partOfSpeech}
                </Typography>
              )}

              <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.6, mb: 3 }}>
                <strong>Definition:</strong> {challenge.definition}
              </Typography>

              {challenge.examples && challenge.examples.length > 0 && (
                <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.02)', borderLeft: '3px solid #eab308' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    "{challenge.examples[0]}"
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Mini-Quiz Card */}
          {challenge.quizzes && challenge.quizzes.length > 0 && (
            <Card sx={{ p: 4, borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent' }}>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                  Today's Mini-Task
                </Typography>

                {graded && (
                  <Alert 
                    severity={isCorrect ? 'success' : 'error'} 
                    sx={{ mb: 4, borderRadius: 3 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {isCorrect ? 'Correct! Well done.' : 'Oops! That is incorrect.'}
                    </Typography>
                  </Alert>
                )}

                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  {challenge.quizzes[0].question}
                </Typography>

                <RadioGroup
                  value={selectedAnswer}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                >
                  <Grid container spacing={2}>
                    {challenge.quizzes[0].options.map((option: string, idx: number) => {
                      let optionColor = 'inherit';
                      if (graded) {
                        if (option === challenge.quizzes[0].correctAnswer) optionColor = 'success.main';
                        else if (selectedAnswer === option) optionColor = 'error.main';
                      }

                      return (
                        <Grid item xs={12} key={idx}>
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              borderRadius: 2, 
                              border: '1px solid rgba(255,255,255,0.04)',
                              backgroundColor: selectedAnswer === option ? 'rgba(234, 179, 8, 0.08)' : 'transparent',
                              color: optionColor
                            }}
                          >
                            <FormControlLabel
                              value={option}
                              control={<Radio color="warning" disabled={graded} />}
                              label={option}
                              sx={{ width: '100%', m: 0 }}
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </RadioGroup>

                {graded && challenge.quizzes[0].explanation && (
                  <Box sx={{ mt: 3, p: 2, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.02)', borderLeft: '3px solid #eab308' }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Explanation:</strong> {challenge.quizzes[0].explanation}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                  {!graded ? (
                    <Button 
                      variant="contained" 
                      color="warning" 
                      fullWidth 
                      onClick={handleGrade}
                      disabled={!selectedAnswer}
                    >
                      Check Answer
                    </Button>
                  ) : (
                    <Button 
                      variant="outlined" 
                      color="warning" 
                      fullWidth 
                      onClick={handleReset}
                    >
                      Try Again
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Container>
  );
};

export default DailyChallenge;
